// ============================================================================
// CrewAhead Maritime Platform - Accounts Admin API Functions
// ============================================================================
// Admin user management endpoints under /accounts/admin/users.

import { apiClient, handleApiError } from './client';
import { API_ENDPOINTS } from '../utils/constants';
import type {
  AdminUserDetail,
  AdminUserListResponse,
  AdminUserStatsResponse,
  UserChangeLogEntry,
} from '../types';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AdminListUsersParams {
  role?: string;
  isActive?: boolean;
  isVerified?: boolean;
  onboardingStatus?: string;
  search?: string;
  dateJoinedAfter?: string;
  dateJoinedBefore?: string;
  ordering?: string;
  page?: number;
  pageSize?: number;
}

export interface AdminUpdateUserData {
  firstName?: string;
  lastName?: string;
  phone?: string;
  isVerified?: boolean;
}

export interface ChangeRoleData {
  role: string;
  instituteName?: string;
  companyName?: string;
}

export interface ChangeRoleResponse {
  detail: string;
  role: string;
}

export interface StatusResponse {
  detail: string;
  isActive: boolean;
}

// ─── Admin Users ─────────────────────────────────────────────────────────────

/**
 * GET /accounts/admin/users
 *
 * List platform users with filtering and pagination.
 */
export async function adminListUsers(params?: AdminListUsersParams): Promise<AdminUserListResponse> {
  try {
    const query = new URLSearchParams();
    if (params?.role) query.set('role', params.role);
    if (params?.isActive !== undefined) query.set('is_active', String(params.isActive));
    if (params?.isVerified !== undefined) query.set('is_verified', String(params.isVerified));
    if (params?.onboardingStatus) query.set('onboarding_status', params.onboardingStatus);
    if (params?.search) query.set('search', params.search);
    if (params?.dateJoinedAfter) query.set('date_joined_after', params.dateJoinedAfter);
    if (params?.dateJoinedBefore) query.set('date_joined_before', params.dateJoinedBefore);
    if (params?.ordering) query.set('ordering', params.ordering);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.ACCOUNTS.ADMIN.USERS}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<AdminUserListResponse>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /accounts/admin/users?counts_only=true
 *
 * Return aggregate user counts across the full platform queryset.
 */
export async function adminGetUserStats(): Promise<AdminUserStatsResponse> {
  try {
    const response = await apiClient.get<AdminUserStatsResponse>(
      `${API_ENDPOINTS.ACCOUNTS.ADMIN.USERS}?counts_only=true`
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /accounts/admin/users/{id}
 *
 * Fetch a single user with profile summary.
 */
export async function adminGetUser(id: string | number): Promise<AdminUserDetail> {
  try {
    const response = await apiClient.get<AdminUserDetail>(
      API_ENDPOINTS.ACCOUNTS.ADMIN.USER_DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * PATCH /accounts/admin/users/{id}
 *
 * Update limited admin-editable fields.
 */
export async function adminUpdateUser(
  id: string | number,
  data: AdminUpdateUserData
): Promise<AdminUserDetail> {
  try {
    const response = await apiClient.patch<AdminUserDetail>(
      API_ENDPOINTS.ACCOUNTS.ADMIN.USER_DETAIL(id),
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /accounts/admin/users/{id}/suspend
 *
 * Suspend a user account and blacklist tokens.
 */
export async function adminSuspendUser(id: string | number): Promise<StatusResponse> {
  try {
    const response = await apiClient.post<StatusResponse>(
      API_ENDPOINTS.ACCOUNTS.ADMIN.SUSPEND(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /accounts/admin/users/{id}/reactivate
 *
 * Reactivate a suspended user account.
 */
export async function adminReactivateUser(id: string | number): Promise<StatusResponse> {
  try {
    const response = await apiClient.post<StatusResponse>(
      API_ENDPOINTS.ACCOUNTS.ADMIN.REACTIVATE(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /accounts/admin/users/{id}/change-role
 *
 * Change a user's role. Only permitted for pre-onboarding users with no profile data.
 */
export async function adminChangeUserRole(
  id: string | number,
  data: ChangeRoleData
): Promise<ChangeRoleResponse> {
  try {
    const response = await apiClient.post<ChangeRoleResponse>(
      API_ENDPOINTS.ACCOUNTS.ADMIN.CHANGE_ROLE(id),
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export interface ApproveInstituteData {
  action: 'approve' | 'reject';
  reason?: string;
}

export interface ApproveInstituteResponse {
  id: number;
  instituteName: string;
  approvalStatus: string;
  approvalNotes: string | null;
  isVerified: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * POST /accounts/admin/users/{id}/approve-institute
 *
 * Approve or reject an institute profile owned by the user.
 */
export async function adminApproveInstitute(
  id: string | number,
  data: ApproveInstituteData
): Promise<ApproveInstituteResponse> {
  try {
    const response = await apiClient.post<ApproveInstituteResponse>(
      API_ENDPOINTS.ACCOUNTS.ADMIN.APPROVE_INSTITUTE(id),
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /accounts/admin/users/{id}/audit-log
 *
 * Fetch the audit log for a user.
 */
export async function adminGetUserAuditLog(id: string | number): Promise<{
  count: number;
  next: string | null;
  previous: string | null;
  results: UserChangeLogEntry[];
}> {
  try {
    const response = await apiClient.get<{
      count: number;
      next: string | null;
      previous: string | null;
      results: UserChangeLogEntry[];
    }>(API_ENDPOINTS.ACCOUNTS.ADMIN.AUDIT_LOG(id));
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Platform Configuration ──────────────────────────────────────────────────

export interface SiteConfig {
  platformName: string;
  supportEmail: string;
  defaultTimezone: string;
  defaultLanguage: string;
  enforceStrongPasswords: boolean;
  updatedAt: string;
  updatedByEmail: string | null;
}

export interface SiteConfigUpdate {
  platformName?: string;
  supportEmail?: string;
  defaultTimezone?: string;
  defaultLanguage?: string;
  enforceStrongPasswords?: boolean;
}

/**
 * GET /accounts/admin/settings/config
 *
 * Fetch the singleton platform configuration (admin only).
 */
export async function getSiteConfig(): Promise<SiteConfig> {
  try {
    const response = await apiClient.get<SiteConfig>(
      API_ENDPOINTS.ACCOUNTS.ADMIN.SITE_CONFIG
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * PATCH /accounts/admin/settings/config
 *
 * Partially update the singleton platform configuration (admin only).
 */
export async function updateSiteConfig(data: SiteConfigUpdate): Promise<SiteConfig> {
  try {
    const response = await apiClient.patch<SiteConfig>(
      API_ENDPOINTS.ACCOUNTS.ADMIN.SITE_CONFIG,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}
